import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBottomBar } from '../../context/BottomBarContext';
import { Sheet } from '../common/Sheet';

interface NavItem {
  path: string;
  label: string;
  permission: string | null;
  icon: JSX.Element;
}

const icon = (d: string) => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={d} />
  </svg>
);

/**
 * The bar itself. Three destinations and an overflow.
 *
 * Nine tabs gave each 41px on a 375px screen — under Apple's 44pt minimum and
 * nearly double Material's five-destination cap — and only fit by shrinking
 * labels to 11px and abbreviating "Maintenance" to "Maint". What survives the
 * cut is what a phone is actually opened for: what is broken, what happened,
 * and how it has been. Everything else is authoring or configuration work.
 */
const primary: NavItem[] = [
  { path: '/', label: 'Status', permission: null, icon: icon('M3 12h4l3 8 4-16 3 8h4') },
  { path: '/incidents', label: 'Incidents', permission: 'services.view', icon: icon('M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z') },
  { path: '/reports', label: 'Reports', permission: 'services.view', icon: icon('M3 3v18h18M7 15l4-4 3 3 5-6') },
];

/** Reached through More, where there is room for the unabbreviated names. */
const secondary: NavItem[] = [
  { path: '/services/new', label: 'Add service', permission: 'services.create', icon: icon('M12 5v14M5 12h14') },
  { path: '/maintenance', label: 'Maintenance', permission: 'services.view', icon: icon('M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z') },
  { path: '/notifications', label: 'Notifications', permission: 'notifications.view', icon: icon('M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2c0 .5-.2 1-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9') },
  { path: '/groups', label: 'Groups', permission: 'groups.view', icon: icon('M4 6h16M4 12h16M4 18h10') },
  { path: '/status-pages', label: 'Status pages', permission: 'status_pages.view', icon: icon('M4 5h16v14H4zM4 9h16M8 13h8') },
  { path: '/config', label: 'Config', permission: 'config.view', icon: icon('M10.3 4.3a1 1 0 011.4 0l.7.7a1 1 0 001 .3l1-.3a1 1 0 011.2.8l.2 1a1 1 0 00.7.7l1 .2a1 1 0 01.8 1.2l-.3 1a1 1 0 00.3 1l.7.7a1 1 0 010 1.4l-.7.7a1 1 0 00-.3 1l.3 1a1 1 0 01-.8 1.2l-1 .2a1 1 0 00-.7.7l-.2 1a1 1 0 01-1.2.8l-1-.3a1 1 0 00-1 .3l-.7.7a1 1 0 01-1.4 0l-.7-.7a1 1 0 00-1-.3l-1 .3a1 1 0 01-1.2-.8l-.2-1a1 1 0 00-.7-.7l-1-.2a1 1 0 01-.8-1.2l.3-1a1 1 0 00-.3-1l-.7-.7a1 1 0 010-1.4l.7-.7a1 1 0 00.3-1l-.3-1a1 1 0 01.8-1.2l1-.2a1 1 0 00.7-.7l.2-1a1 1 0 011.2-.8l1 .3a1 1 0 001-.3l.7-.7zM15 12a3 3 0 11-6 0 3 3 0 016 0z') },
];

const isActive = (path: string, pathname: string) =>
  path === '/' ? pathname === '/' : pathname.startsWith(path);

const tabClass = (active: boolean) =>
  `flex flex-col items-center justify-center gap-1 w-full min-h-[3.25rem] py-2 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 ${
    active ? 'text-brand-600 dark:text-brand-400' : 'text-gray-500 dark:text-gray-400'
  }`;

interface Props {
  /** Services currently down, badged onto Status. */
  downCount?: number;
}

/**
 * Bottom tab bar for viewports below `md`, where the header nav is hidden.
 * Without this the dashboard, service form, groups, notifications,
 * maintenance and config pages have no reachable entry point on a phone.
 */
export function MobileNav({ downCount = 0 }: Props) {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const { claimed } = useBottomBar();
  const [moreOpen, setMoreOpen] = useState(false);

  // A page is running a contextual toolbar in this slot; there is only room
  // for one, and stacking them is what broke before.
  if (claimed) return null;

  const allowed = (i: NavItem) => i.permission === null || hasPermission(i.permission);
  const tabs = primary.filter(allowed);
  const more = secondary.filter(allowed);
  if (tabs.length === 0 && more.length === 0) return null;

  const moreActive = more.some(i => isActive(i.path, location.pathname));

  return (
    <>
      <nav
        aria-label="Main"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 pb-[env(safe-area-inset-bottom)]"
      >
        <ul className="flex items-stretch">
          {tabs.map(item => {
            const active = isActive(item.path, location.pathname);
            const badge = item.path === '/' && downCount > 0 ? downCount : 0;
            return (
              <li key={item.path} className="flex-1">
                <Link
                  to={item.path}
                  aria-current={active ? 'page' : undefined}
                  // The badge is a graphic; name the tab for what it says.
                  aria-label={badge ? `${item.label}, ${badge} service${badge === 1 ? '' : 's'} down` : undefined}
                  className={tabClass(active)}
                >
                  <span className="relative">
                    {item.icon}
                    {badge > 0 && (
                      <span
                        aria-hidden="true"
                        className="absolute -top-1.5 -right-2.5 min-w-[1.125rem] h-[1.125rem] px-1 flex items-center justify-center rounded-full bg-status-down text-white text-[0.625rem] font-semibold tabular-nums"
                      >
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </span>
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}

          {more.length > 0 && (
            <li className="flex-1">
              <button
                type="button"
                onClick={() => setMoreOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={moreOpen}
                className={tabClass(moreActive)}
              >
                {icon('M5 12h.01M12 12h.01M19 12h.01')}
                <span>More</span>
              </button>
            </li>
          )}
        </ul>
      </nav>

      <Sheet open={moreOpen} onClose={() => setMoreOpen(false)} title="More">
        <ul className="py-1">
          {more.map(item => {
            const active = isActive(item.path, location.pathname);
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => setMoreOpen(false)}
                  aria-current={active ? 'page' : undefined}
                  className={`flex items-center gap-3 px-4 min-h-[3rem] text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 ${
                    active
                      ? 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="shrink-0 text-gray-400 dark:text-gray-500">{item.icon}</span>
                  <span className="flex-1">{item.label}</span>
                  <svg className="w-4 h-4 shrink-0 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </li>
            );
          })}
        </ul>
      </Sheet>
    </>
  );
}
